import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DropdownMenu } from 'reactstrap';

interface PortalDropdownProps {
    targetRef: React.RefObject<HTMLElement>;
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
}

const PortalDropdown: React.FC<PortalDropdownProps> = ({ targetRef, isOpen, onClose, children }) => {
    const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: -100 });
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (targetRef.current && isOpen) {
            const rect = targetRef.current.getBoundingClientRect();
            const dropdownWidth = 200; // Approximate dropdown width
            const viewportWidth = window.innerWidth;

            let left = rect.left + window.scrollX;

            // Check if dropdown would overflow right edge
            if (left + dropdownWidth > viewportWidth) {
                left = viewportWidth - dropdownWidth - 10; // 10px margin from edge
            }

            // Ensure dropdown doesn't go off the left edge
            if (left < 10) {
                left = 10;
            }

            setCoords({
                top: rect.bottom + window.scrollY,
                left
            });
        }
    }, [isOpen, targetRef]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                targetRef.current &&
                !targetRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose, targetRef]);

    if (!isOpen) return null;

    return createPortal(
        <div
            ref={dropdownRef}
            style={{
                position: 'absolute',
                top: coords.top,
                left: coords.left,
                zIndex: 3000,
                boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                borderRadius: 4,
                overflow: 'hidden',
            }}
        >
            <DropdownMenu className="show" style={{ position: 'static' }}>
                {children}
            </DropdownMenu>
        </div>,
        document.body
    );
};

export default PortalDropdown;
